import { createFileRoute, Link } from '@tanstack/react-router';
import { useOrganization } from '@/hooks/useOrganization';
import { useProjects } from '@/hooks/useProjects';
import { CreateProjectDrawer } from '@/components/projects/CreateProjectDrawer';
import type { ApiProject, ProjectsResponse } from '@envase/api/client';
import { loadAuthGuard } from '@/lib/auth-helpers';

export const Route = createFileRoute('/_authenticated/org/$orgId/projects/')({
  loader: loadAuthGuard<{ orgId: string }>(async ({ params, context, ensureQueryData, sessionReady }) => {
    const { orgId } = params;
    const { trpc } = context;

    if (sessionReady) {
      await ensureQueryData(
        trpc.projects.getAll.queryOptions(undefined),
      );
    }
    
    return { orgId };
  }),
  component: ProjectsPage,
});

function ProjectsPage() {
  const { orgId } = Route.useParams();
  const { organization } = useOrganization(orgId);
  const { data, isLoading, refetch } = useProjects();
  const allProjects: ApiProject[] = (data as ProjectsResponse | undefined)?.projects || [];
  
  // Filter projects by organization
  const projects = allProjects.filter((project: ApiProject) => project.organizationId === organization?.id);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Projects</h2>
          <p className="text-gray-600 mt-1">
            Manage your environment variable projects
          </p>
        </div>
        <CreateProjectDrawer onProjectCreated={refetch} />
      </div>

      {isLoading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
          <p className="text-gray-600 mt-4">Loading projects...</p>
        </div>
      ) : projects && projects.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {projects.map((project) => (
            <Link
              key={project.id}
              to="/org/$orgId/projects/$id"
              params={{ orgId, id: project.id }}
              className="bg-white p-6 rounded-lg shadow hover:shadow-lg transition"
              data-testid={`project-card-${project.slug}`}
            >
              <h3 className="text-lg font-semibold mb-2">{project.name}</h3>
              <p className="text-gray-600 text-sm">
                {project.description || 'No description'}
              </p>
              <div className="mt-4 flex items-center justify-between text-sm">
                <span className="text-gray-500">/{project.slug}</span>
                <span className="text-purple-600 font-medium">View →</span>
              </div>
            </Link>
          ))}
        </div>
      ) : (
        <div className="bg-white p-12 rounded-lg shadow text-center">
          <h3 className="text-lg font-semibold mb-2">No projects yet</h3>
          <p className="text-gray-600 mb-6">
            Create your first project to start managing environment variables
          </p>
          <CreateProjectDrawer onProjectCreated={refetch} />
        </div>
      )}
    </div>
  );
}

